(function () {
  'use strict';

  /*
   * jb-video-row.js - Jarri Boottii product video row
   *
   * Deliberately tiny: one delegated click listener, one IntersectionObserver
   * and one matchMedia listener for the whole row. No library, no per-video
   * listeners, no polling.
   *
   * What it does:
   *   - nothing downloads until a tap. The <video> elements ship with
   *     preload="none", so the browser fetches only the poster image.
   *   - tapping a tile reveals native controls and starts that video.
   *   - starting one video pauses whichever was playing, so four videos can
   *     never talk over each other.
   *   - a video that scrolls out of view pauses itself.
   *   - audio can only ever begin from a real user gesture: there is no
   *     autoplay and no muted-autoplay path anywhere in this file.
   *   - on mobile only, the row is relocated to sit directly above the
   *     "Order With Confidence" block (see MOBILE PLACEMENT below).
   */

  if (window.__jbVideoRow) return;
  window.__jbVideoRow = true;

  var current = null;

  function frameOf(video) {
    return video.closest('.jb-vidrow__frame');
  }

  function pause(video) {
    if (!video) return;
    try { video.pause(); } catch (e) {}
    var f = frameOf(video);
    if (f) f.setAttribute('data-jb-v-active', 'false');
  }

  function play(video) {
    if (current && current !== video) pause(current);
    current = video;

    var f = frameOf(video);
    if (f) f.setAttribute('data-jb-v-active', 'true');

    /* Native controls appear only once the shopper has chosen to watch, so the
       resting state stays a clean poster + play button. */
    video.controls = true;

    var p = video.play();
    if (p && typeof p.catch === 'function') {
      p.catch(function () {
        /* Autoplay policies can still refuse in rare cases; fall back to the
           poster state rather than leaving a dead frame. */
        if (f) f.setAttribute('data-jb-v-active', 'false');
        if (current === video) current = null;
      });
    }
  }

  document.addEventListener('click', function (e) {
    var frame = e.target.closest ? e.target.closest('.jb-vidrow__frame') : null;
    if (!frame) return;

    var video = frame.querySelector('video');
    if (!video) return;

    /* Once controls are showing, let the browser own the clicks (scrubbing,
       volume, fullscreen) instead of toggling underneath the user. */
    if (frame.getAttribute('data-jb-v-active') === 'true') return;

    e.preventDefault();
    play(video);
  });

  /* Keyboard: the frame is a <button>, so Enter/Space already fire click. */

  document.addEventListener('play', function (e) {
    /* Catches plays started from the native controls too. */
    var v = e.target;
    if (!v || v.tagName !== 'VIDEO' || !v.closest('.jb-vidrow')) return;
    if (current && current !== v) pause(current);
    current = v;
  }, true);

  if ('IntersectionObserver' in window) {
    var io = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          var en = entries[i];
          if (!en.isIntersecting && !en.target.paused) pause(en.target);
        }
      },
      { threshold: 0.25 }
    );

    var attach = function () {
      var vids = document.querySelectorAll('.jb-vidrow video');
      for (var i = 0; i < vids.length; i++) io.observe(vids[i]);
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', attach);
    } else {
      attach();
    }
  }

  /* =====================================================================
   * MOBILE PLACEMENT
   * =====================================================================
   * Desktop keeps the row exactly where Liquid renders it: inside
   * .jb-under-gallery, directly beneath the product image gallery in the left
   * column. Nothing below touches that.
   *
   * On mobile the row has to sit between the delivery block and "Order With
   * Confidence". Those two live inside .product-details, while the row lives
   * in .jb-under-gallery - a different parent - so CSS `order` cannot
   * interleave them. Rather than render the four videos twice and hide one
   * copy (which would mean duplicate DOM, duplicate accessible names and
   * duplicate poster requests), the single existing node is moved.
   *
   * That keeps exactly ONE video row on the page: one set of <video> elements,
   * one set of posters, no extra network requests, and every attribute and
   * event handler travels with it.
   *
   * A comment node marks the desktop home position so the row can be put back
   * verbatim if the viewport crosses back over the breakpoint.
   *
   * 749px matches the theme's own mobile breakpoint - the width below which
   * the product grid collapses to a single stacked column.
   *
   * WHEN THIS RUNS (performance)
   * ----------------------------
   * The functions below are normally invoked by the small inline script that
   * layout/theme.liquid renders immediately after the product content. That
   * script runs synchronously while the HTML is still being parsed, so the
   * final mobile position is already in place at first paint.
   *
   * This file is loaded with `defer`, which means it used to execute at
   * DOMContentLoaded - i.e. AFTER the browser had already painted the desktop
   * order. Moving ~500px of content above the product details at that point
   * produced a measured Cumulative Layout Shift of 0.177 on throttled mobile.
   * The bootstrap at the bottom of this file is therefore skipped when the
   * inline script has already done the work, and kept only as a fallback.
   */

  var mq = window.matchMedia('(max-width: 749px)');
  var row = null;
  var home = null;
  var anchor = null;

  function findAnchor() {
    /* The block wrapper around "Order With Confidence", i.e. the element that
       is a direct sibling of the delivery block. */
    var protect = document.querySelector('.jb-protect');
    if (!protect) return null;
    var inner = protect.closest('.jb-c');
    return inner ? inner.parentElement : null;
  }

  function applyPlacement() {
    if (!row || !home || !anchor || !anchor.parentNode) return;

    var wantMobile = mq.matches;
    var isMobileNow = row.parentNode === anchor.parentNode && row.nextSibling === anchor;
    var isDesktopNow = row.previousSibling === home;

    if (wantMobile ? isMobileNow : isDesktopNow) return;

    /* Reparenting a media element resets it, so stop playback first rather
       than leaving a half-loaded video mid-move. */
    if (current) {
      pause(current);
      current = null;
    }

    if (wantMobile) {
      anchor.parentNode.insertBefore(row, anchor);
    } else {
      home.parentNode.insertBefore(row, home.nextSibling);
    }
  }

  function setupPlacement() {
    /* Move the shared wrapper when it exists, so the before/after comparison
       travels with the video row and stays directly above it on mobile.
       Falls back to the bare row if the wrapper is ever absent. */
    row = document.querySelector('.jb-pdp-extras') || document.querySelector('.jb-vidrow');
    if (!row) return;

    anchor = findAnchor();
    /* No anchor (e.g. a product without that block) - leave the row where
       Liquid put it rather than guessing. */
    if (!anchor || !anchor.parentNode) return;

    if (!home) {
      home = document.createComment('jb-vidrow-desktop-home');
      row.parentNode.insertBefore(home, row);
    }

    applyPlacement();
  }


  /* ------------------------------------------------------------------
   * MOBILE PLACEMENT - BEFORE/AFTER COMPARISON
   *
   * On mobile the comparison section belongs directly under the product
   * gallery, above the product title. The video row keeps its own mobile
   * placement above "Order With Confidence" (handled above); only .jb-ba
   * is lifted out of .jb-pdp-extras.
   *
   * The existing element is MOVED, never cloned, so jb-beforeafter.js keeps
   * the pointer/keyboard listeners it already bound to the stage and its
   * stage.__jbBaReady guard is never re-triggered. There is therefore only
   * ever one slider in the DOM.
   *
   * Desktop is untouched: below 750px only. A comment node marks the desktop
   * home so the section returns to exactly where Liquid rendered it.
   * ------------------------------------------------------------------ */
  var ba = null;
  var baHome = null;
  var baAnchor = null;

  function applyBaPlacement() {
    if (!ba || !baHome || !baAnchor || !baAnchor.parentNode) return;

    var wantMobile = mq.matches;
    var isMobileNow = ba.previousSibling === baAnchor;
    var isDesktopNow = ba.previousSibling === baHome;

    if (wantMobile ? isMobileNow : isDesktopNow) return;

    if (wantMobile) {
      /* directly after the gallery, as a sibling inside the product grid */
      baAnchor.parentNode.insertBefore(ba, baAnchor.nextSibling);
    } else {
      baHome.parentNode.insertBefore(ba, baHome.nextSibling);
    }
  }

  function setupBaPlacement() {
    ba = document.querySelector('.jb-ba');
    if (!ba) return;

    baAnchor = document.querySelector('.product-information__media');
    if (!baAnchor || !baAnchor.parentNode) return;

    if (!baHome) {
      baHome = document.createComment('jb-ba-desktop-home');
      ba.parentNode.insertBefore(baHome, ba);
    }

    applyBaPlacement();
  }

  function applyBoth() {
    applyPlacement();
    applyBaPlacement();
  }

  function setupBoth() {
    setupPlacement();
    setupBaPlacement();
  }

  /* Expose for the inline pre-paint script in layout/theme.liquid. */
  window.__jbPlacement = { setup: setupBoth, apply: applyBoth };

  /* ------------------------------------------------------------------
   * FALLBACK BOOTSTRAP
   *
   * Skipped when the inline pre-paint script in layout/theme.liquid has
   * already claimed placement (window.__jbPlace === true). It is kept so that
   * any template or context where that inline script is absent still gets the
   * correct mobile layout - just with the old post-paint timing.
   * ------------------------------------------------------------------ */
  if (!window.__jbPlace) {
    if (mq.addEventListener) {
      mq.addEventListener('change', applyBoth);
    } else if (mq.addListener) {
      mq.addListener(applyBoth);
    }

    /* Belt and braces. Some environments (and some older mobile browsers on
       orientation change) do not deliver the MediaQueryList "change" event
       reliably, so a debounced resize handler re-checks placement too.
       applyPlacement() is a no-op when the row is already where it should be,
       so this costs nothing on a normal resize. */
    var resizeTimer = null;
    window.addEventListener(
      'resize',
      function () {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(applyBoth, 150);
      },
      { passive: true }
    );

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', setupBoth);
    } else {
      setupBoth();
    }

    /* The theme editor re-renders sections; re-resolve the nodes if that happens. */
    document.addEventListener('shopify:section:load', function () {
      home = null;
      baHome = null;
      setupBoth();
    });
  }
})();
